import React, { useEffect, useMemo, useRef, useState } from 'react';

interface AvailableVideo {
  videoId: string;
  title: string;
}

interface VideoMultiSelectProps {
  availableVideos: AvailableVideo[];
  selected: string[];
  onChange: (next: string[]) => void;
  max?: number;
  placeholder?: string;
  compact?: boolean;
  // Hide the chips for currently-selected videos. The selection still drives
  // the cap and is excluded from suggestions, but the chip row is empty.
  // Used when the selection is a system-applied default the user hasn't
  // touched yet.
  hideSelectedChips?: boolean;
}

const truncate = (s: string, n: number) => (s.length > n ? s.slice(0, n) + '…' : s);

const VideoMultiSelect: React.FC<VideoMultiSelectProps> = ({
  availableVideos,
  selected,
  onChange,
  max = 10,
  placeholder = 'Search videos to add…',
  compact = false,
  hideSelectedChips = false
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const titleByVideoId = useMemo(() => {
    const map: Record<string, string> = {};
    availableVideos.forEach(v => { map[v.videoId] = v.title; });
    return map;
  }, [availableVideos]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const selectedSet = new Set(selected);
    return availableVideos
      // When chips are hidden the user can't see what's already selected,
      // so the seeded selections must remain pickable in the suggestions.
      .filter(v => hideSelectedChips || !selectedSet.has(v.videoId))
      .filter(v => (q ? v.title.toLowerCase().includes(q) : true))
      .slice(0, 20);
  }, [availableVideos, selected, query, hideSelectedChips]);

  useEffect(() => {
    setActiveIdx(0);
  }, [query, isOpen]);

  // Click outside to close
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // When chips are hidden the cap is meaningless — the user hasn't
  // intentionally selected anything yet, so let them pick freely.
  const atLimit = !hideSelectedChips && selected.length >= max;

  const addVideo = (videoId: string) => {
    // In `hideSelectedChips` mode the user can't see the seeded selection, so
    // a click means "start filtering by just this one." Send only the
    // clicked id; the parent will switch out of default mode.
    if (hideSelectedChips) {
      onChange([videoId]);
      setQuery('');
      return;
    }
    if (selected.includes(videoId)) return;
    if (selected.length >= max) return;
    onChange([...selected, videoId]);
    setQuery('');
  };

  const removeVideo = (videoId: string) => {
    onChange(selected.filter(id => id !== videoId));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      setActiveIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = suggestions[activeIdx];
      if (pick && !atLimit) addVideo(pick.videoId);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Backspace' && query === '' && selected.length > 0 && !hideSelectedChips) {
      removeVideo(selected[selected.length - 1]);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {!compact && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Videos
          </span>
          <span style={{ fontSize: 12, color: atLimit ? '#B45309' : '#6B7280' }}>
            {selected.length} / {max}
          </span>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: compact ? 'nowrap' : 'wrap',
          gap: 6,
          padding: compact ? '4px 6px' : 6,
          border: '1px solid #D1D5DB',
          borderRadius: compact ? 999 : 12,
          backgroundColor: '#FFFFFF',
          minHeight: compact ? 36 : 44,
          height: compact ? 36 : undefined,
          alignItems: 'center',
          overflow: compact ? 'hidden' : undefined
        }}
        onClick={() => setIsOpen(true)}
      >
        {compact && !hideSelectedChips && (
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            color: atLimit ? '#B45309' : '#6B7280',
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            padding: '0 4px 0 6px',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}>
            Videos {selected.length}/{max}
          </span>
        )}
        {!hideSelectedChips && selected.map(videoId => (
          <span
            key={videoId}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 6px 4px 10px',
              backgroundColor: '#EEF2FF',
              color: '#3730A3',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 500,
              maxWidth: 220,
              flexShrink: 0
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {truncate(titleByVideoId[videoId] || videoId, 28)}
            </span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeVideo(videoId); }}
              aria-label={`Remove ${titleByVideoId[videoId] || videoId}`}
              style={{
                width: 18,
                height: 18,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                borderRadius: '50%',
                backgroundColor: 'rgba(55,48,163,0.12)',
                color: '#3730A3',
                cursor: 'pointer',
                fontSize: 12,
                lineHeight: 1
              }}
            >
              ×
            </button>
          </span>
        ))}

        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={selected.length === 0 ? placeholder : ''}
          style={{
            flex: 1,
            minWidth: 160,
            border: 'none',
            outline: 'none',
            fontSize: 13,
            padding: '6px 8px',
            backgroundColor: 'transparent'
          }}
        />
      </div>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: 12,
            boxShadow: '0 12px 24px -12px rgba(0,0,0,0.18), 0 4px 8px -4px rgba(0,0,0,0.08)',
            zIndex: 30,
            maxHeight: 280,
            overflowY: 'auto'
          }}
        >
          {atLimit && (
            <div style={{ padding: '10px 12px', fontSize: 12, color: '#B45309', backgroundColor: '#FFFBEB', borderBottom: '1px solid #FDE68A' }}>
              Limit reached — remove a video to add another.
            </div>
          )}
          {suggestions.length === 0 ? (
            <div style={{ padding: '12px 14px', fontSize: 13, color: '#6B7280' }}>
              {query ? 'No matching videos.' : 'No more videos to add.'}
            </div>
          ) : (
            suggestions.map((s, i) => {
              const disabled = atLimit;
              const active = i === activeIdx;
              return (
                <button
                  key={s.videoId}
                  type="button"
                  disabled={disabled}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => !disabled && addVideo(s.videoId)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 14px',
                    fontSize: 13,
                    border: 'none',
                    backgroundColor: active && !disabled ? '#F3F4F6' : 'transparent',
                    color: disabled ? '#9CA3AF' : '#111827',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.6 : 1
                  }}
                >
                  {s.title}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default VideoMultiSelect;
